import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useVehicleTrackingSummary, VehicleTrackingAlert } from "../api/vehicleTracking";
import { NotificationItem, useNotifications } from "../api/notifications";
import LessonReportModal from "../components/lesson-reports/LessonReportModal";
import { formatDailyDateDmY } from "../utils/dates";
import {
  notificationTypeToHeading,
  parseVehicleNotificationTitle,
  sanitizeVehicleNotificationMessage,
  vehicleAlertHeading,
} from "../utils/vehicleAlertHeadings";
import "./DashboardPage.css";

type OccupancyBucket = {
  filled: number;
  empty: number;
  leave: number;
  blocked: number;
  bookable: number;
  occupancy_percent: number;
};

type UpcomingItem = {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  lesson_type: string;
  status: string;
  student_name: string;
  instructor_name: string;
  vehicle_plate: string;
};

type TodayLesson = UpcomingItem & {
  has_report: boolean;
  report_attended: boolean | null;
  report_id: string | null;
};

type DashboardOverview = {
  meta: {
    today: string;
    day: string;
    week_from: string;
    week_to: string;
    month_from: string;
    month_to: string;
    day_offset: number;
    week_offset: number;
    month_offset: number;
  };
  occupancy: {
    today: OccupancyBucket;
    live_today: OccupancyBucket;
    week: OccupancyBucket;
    month: OccupancyBucket;
  };
  directory: {
    active_instructors: number;
    active_vehicles: number;
    active_students: number;
    today_appointments: number;
  };
  upcoming: UpcomingItem[];
  today_lessons: TodayLesson[];
  today_reports_summary: {
    total_lessons: number;
    with_report: number;
    pending_report: number;
  };
};

const LESSON_TYPE_LABEL: Record<string, string> = {
  normal: "Normal",
  extra: "Ek",
  special: "Özel",
};

function formatNotifyTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

type DashboardNotifyItem = {
  key: string;
  notificationId?: string;
  plate: string;
  label: string;
  message: string;
  time: string;
  severity: "critical" | "warning" | "info";
  isUnread: boolean;
};

function vehicleAlertToNotifyItem(alert: VehicleTrackingAlert, generatedAt: string): DashboardNotifyItem {
  const levelTone =
    alert.alert_level === "expired" || alert.alert_level === "very_soon" ? "critical" : "warning";
  return {
    key: `${alert.vehicle_id}-${alert.alert_type}-${alert.alert_level}`,
    plate: alert.plate_no,
    label: vehicleAlertHeading(alert.alert_type, alert.alert_level),
    message: sanitizeVehicleNotificationMessage(
      alert.message ||
        `${alert.brand_model || "Araç"} aracının ${formatAlertMetric(alert).toLowerCase()}.`,
    ),
    time: generatedAt,
    severity: levelTone,
    isUnread: true,
  };
}

function notificationToNotifyItem(item: NotificationItem): DashboardNotifyItem {
  const { plate, heading } = parseVehicleNotificationTitle(item.title);
  const severity =
    item.severity === "critical" || item.severity === "high"
      ? "critical"
      : item.severity === "warning" || item.severity === "medium"
        ? "warning"
        : "info";
  return {
    key: item.id,
    notificationId: item.id,
    plate,
    label: notificationTypeToHeading(item.type) || heading || item.type,
    message: sanitizeVehicleNotificationMessage(item.message),
    time: item.created_at,
    severity,
    isUnread: !item.read_at,
  };
}

function formatAlertMetric(alert: VehicleTrackingAlert): string {
  if (alert.alert_type === "service") {
    if (alert.km_left === null) return "-";
    if (alert.km_left < 0) return `${Math.abs(alert.km_left).toLocaleString("tr-TR")} km geçildi`;
    return `${alert.km_left.toLocaleString("tr-TR")} km kaldı`;
  }
  if (alert.days_left === null) return "-";
  if (alert.days_left < 0) return `${Math.abs(alert.days_left)} gün geçti`;
  if (alert.days_left === 0) return "Bugün";
  return `${alert.days_left} gün kaldı`;
}

function formatTimeHm(iso: string): string {
  return new Date(iso).toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Istanbul",
  });
}

function formatUntilStart(startsAt: string): string {
  const diffMs = new Date(startsAt).getTime() - Date.now();
  if (diffMs <= 0) return "Başladı / devam ediyor";
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `${mins} dk sonra`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} sa ${m} dk sonra` : `${h} sa sonra`;
}

function greetingForHour(): string {
  const h = Number(
    new Intl.DateTimeFormat("tr-TR", { hour: "numeric", hour12: false, timeZone: "Europe/Istanbul" }).format(
      new Date(),
    ),
  );
  if (h < 12) return "Günaydın";
  if (h < 18) return "İyi günler";
  return "İyi akşamlar";
}

function dayCardTitle(offset: number): string {
  if (offset === 0) return "Bugün";
  if (offset === 1) return "Yarın";
  if (offset === -1) return "Dün";
  return "Gün";
}

function weekCardTitle(offset: number): string {
  if (offset === 0) return "Bu Hafta";
  if (offset === 1) return "Sonraki hafta";
  if (offset === -1) return "Önceki hafta";
  return "Hafta";
}

function monthCardTitle(offset: number, monthFrom: string): string {
  if (offset === 0) return "Bu Ay";
  if (offset === 1) return "Sonraki ay";
  if (offset === -1) return "Önceki ay";
  const label = new Date(`${monthFrom}T12:00:00+03:00`).toLocaleDateString("tr-TR", {
    month: "long",
    year: "numeric",
    timeZone: "Europe/Istanbul",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function OccupancyCard({
  title,
  subtitle,
  data,
  accent,
  onPrev,
  onNext,
  navDisabled,
}: {
  title: string;
  subtitle: string;
  data: OccupancyBucket;
  accent: "blue" | "violet" | "teal";
  onPrev: () => void;
  onNext: () => void;
  navDisabled?: boolean;
}) {
  return (
    <article className={`kp-dash-occ kp-dash-occ--${accent}`}>
      <header className="kp-dash-occ__head">
        <div className="kp-dash-occ__head-main">
          <div className="kp-dash-occ__nav">
            <button
              type="button"
              className="kp-dash-occ__nav-btn"
              onClick={onPrev}
              disabled={navDisabled}
              aria-label={`${title} önceki`}
            >
              ‹
            </button>
            <div>
              <h3 className="kp-dash-occ__title">{title}</h3>
              <p className="kp-dash-occ__sub">{subtitle}</p>
            </div>
            <button
              type="button"
              className="kp-dash-occ__nav-btn"
              onClick={onNext}
              disabled={navDisabled}
              aria-label={`${title} sonraki`}
            >
              ›
            </button>
          </div>
        </div>
        <strong className="kp-dash-occ__pct">{data.occupancy_percent}%</strong>
      </header>
      <div
        className="kp-dash-occ__bar"
        role="progressbar"
        aria-valuenow={data.occupancy_percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className="kp-dash-occ__bar-fill" style={{ width: `${data.occupancy_percent}%` }} />
      </div>
      <div className="kp-dash-occ__stats">
        <span>
          <em>{data.filled}</em> dolu
        </span>
        <span>
          <em>{data.empty}</em> boş
        </span>
        <span className="kp-dash-occ__muted">
          {data.bookable > 0 ? `${data.bookable} toplam ders saati` : "Ders saati yok"}
          {data.leave > 0 ? ` · ${data.leave} izinli` : ""}
        </span>
      </div>
    </article>
  );
}

export function DashboardPage() {
  const { session } = useAuth();
  const role = session?.user?.role;
  const canSeeVehicleAlerts = role !== "student";
  const canSeeNotifications =
    role === "tenant_admin" || role === "manager" || role === "office_staff";
  const vehicleAlerts = useVehicleTrackingSummary();
  const notifications = useNotifications(canSeeNotifications);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [occRefreshing, setOccRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lessonReportFor, setLessonReportFor] = useState<TodayLesson | null>(null);
  const [dayOffset, setDayOffset] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const isInitial = !overview;
    if (isInitial) setLoading(true);
    else setOccRefreshing(true);
    setError(null);
    api
      .get<DashboardOverview>("/dashboard/overview", {
        day_offset: String(dayOffset),
        week_offset: String(weekOffset),
        month_offset: String(monthOffset),
      })
      .then((data) => {
        if (!cancelled) setOverview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError("Kontrol paneli verisi yüklenemedi.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setOccRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
    // overview bilerek bagimlilikta yok: yalnizca ofset degisince yenile
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayOffset, weekOffset, monthOffset]);

  const displayName = session?.user?.full_name || session?.user?.email || "Yönetici";
  const tenantLabel = session?.user?.tenant_display_name?.trim() || "Kurum";
  const dayLabel = overview ? formatDailyDateDmY(overview.meta.day || overview.meta.today) : "";
  const liveTodayPct =
    overview?.occupancy.live_today?.occupancy_percent ?? overview?.occupancy.today.occupancy_percent ?? 0;
  const clockLabel = useMemo(
    () =>
      new Date().toLocaleString("tr-TR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Istanbul",
      }),
    [overview?.meta.today],
  );

  const nextUpcoming = overview?.upcoming[0] ?? null;

  const lessonsNeedingReport = useMemo(() => {
    if (!overview) return [];
    const now = Date.now();
    return overview.today_lessons.filter(
      (l) => (l.status === "completed" || new Date(l.ends_at).getTime() <= now) && !l.has_report,
    );
  }, [overview]);

  const criticalVehicleAlerts = useMemo(() => {
    const alerts = vehicleAlerts.data?.alerts ?? [];
    return alerts.filter((a) => a.alert_level === "expired" || a.alert_level === "very_soon");
  }, [vehicleAlerts.data?.alerts]);

  const vehicleAlertCounts = vehicleAlerts.data?.counts;
  const criticalAlertTotal =
    (vehicleAlertCounts?.expired ?? 0) + (vehicleAlertCounts?.very_soon ?? 0);

  const dashboardNotifyItems = useMemo((): DashboardNotifyItem[] => {
    if (canSeeNotifications && notifications.open.length > 0) {
      return notifications.open.slice(0, 12).map(notificationToNotifyItem);
    }
    if (canSeeVehicleAlerts && criticalVehicleAlerts.length > 0) {
      const generatedAt = vehicleAlerts.data?.generated_at ?? new Date().toISOString();
      return criticalVehicleAlerts.slice(0, 12).map((a) => vehicleAlertToNotifyItem(a, generatedAt));
    }
    return [];
  }, [
    canSeeNotifications,
    canSeeVehicleAlerts,
    notifications.open,
    criticalVehicleAlerts,
    vehicleAlerts.data?.generated_at,
  ]);

  const dashboardNotifyUnread = useMemo(
    () => dashboardNotifyItems.filter((i) => i.isUnread),
    [dashboardNotifyItems],
  );
  const dashboardNotifyRead = useMemo(
    () => dashboardNotifyItems.filter((i) => !i.isUnread),
    [dashboardNotifyItems],
  );

  const dashboardNotifyBadgeCount = canSeeNotifications
    ? notifications.unreadCount
    : dashboardNotifyUnread.length;

  const notificationsLoading = canSeeNotifications ? notifications.loading : vehicleAlerts.loading;
  const notificationsError = canSeeNotifications ? notifications.error : vehicleAlerts.error;

  return (
    <div className="kp-dashboard">
      <section className="kp-dash-hero">
        <div className="kp-dash-hero__text">
          <p className="kp-dash-hero__greet">
            {greetingForHour()}, {displayName}
          </p>
          <div className="kp-dash-hero__headline">
            <h1 className="kp-dash-hero__title">{tenantLabel}</h1>
            <span className="kp-dash-hero__badge">Kontrol Paneli</span>
            <span className="kp-dash-hero__date">{clockLabel}</span>
          </div>
          {nextUpcoming && (
            <p className="kp-dash-hero__next">
              Sıradaki ders: <strong>{formatTimeHm(nextUpcoming.starts_at)}</strong> — {nextUpcoming.student_name} (
              {nextUpcoming.instructor_name})
            </p>
          )}
        </div>
        <div className="kp-dash-hero__actions">
          {overview && (
            <div className="kp-dash-hero__chips">
              <span className="kp-dash-hero__chip">
                <strong>{overview.directory.today_appointments}</strong> bugünkü randevu
              </span>
              <span className="kp-dash-hero__chip">
                <strong>{liveTodayPct}%</strong> bugün doluluk
              </span>
            </div>
          )}
          <Link to="/calendar" className="kp-dash-hero__btn kp-dash-hero__btn--ghost">
            Takvimi Aç
          </Link>
        </div>
      </section>

      {loading && <div className="kp-card kp-loading">Kontrol paneli yükleniyor…</div>}
      {error && <div className="kp-card kp-error">{error}</div>}

      {overview && !loading && !error && (
        <>
          <div className={`kp-dash-occ-row${occRefreshing ? " kp-dash-occ-row--refreshing" : ""}`}>
            <OccupancyCard
              title={dayCardTitle(dayOffset)}
              subtitle={dayLabel}
              data={overview.occupancy.today}
              accent="blue"
              navDisabled={occRefreshing}
              onPrev={() => setDayOffset((v) => v - 1)}
              onNext={() => setDayOffset((v) => v + 1)}
            />
            <OccupancyCard
              title={weekCardTitle(weekOffset)}
              subtitle={`${formatDailyDateDmY(overview.meta.week_from)} – ${formatDailyDateDmY(overview.meta.week_to)}`}
              data={overview.occupancy.week}
              accent="violet"
              navDisabled={occRefreshing}
              onPrev={() => setWeekOffset((v) => v - 1)}
              onNext={() => setWeekOffset((v) => v + 1)}
            />
            <OccupancyCard
              title={monthCardTitle(monthOffset, overview.meta.month_from)}
              subtitle={`${formatDailyDateDmY(overview.meta.month_from)} – ${formatDailyDateDmY(overview.meta.month_to)}`}
              data={overview.occupancy.month}
              accent="teal"
              navDisabled={occRefreshing}
              onPrev={() => setMonthOffset((v) => v - 1)}
              onNext={() => setMonthOffset((v) => v + 1)}
            />
          </div>

          <div className="kp-dash-mini-row">
            <div className="kp-dash-mini">
              <span className="kp-dash-mini__label">Aktif kursiyer</span>
              <strong>{overview.directory.active_students}</strong>
            </div>
            <div className="kp-dash-mini">
              <span className="kp-dash-mini__label">Aktif eğitmen</span>
              <strong>{overview.directory.active_instructors}</strong>
            </div>
            <div className="kp-dash-mini">
              <span className="kp-dash-mini__label">Aktif araç</span>
              <strong>{overview.directory.active_vehicles}</strong>
            </div>
            {canSeeVehicleAlerts && (
              <Link
                to="/vehicles"
                className={`kp-dash-mini kp-dash-mini--link${
                  criticalAlertTotal > 0 ? " kp-dash-mini--alert" : ""
                }`}
              >
                <span className="kp-dash-mini__label">Kritik araç uyarısı</span>
                <strong>{vehicleAlerts.loading ? "…" : criticalAlertTotal}</strong>
              </Link>
            )}
            <div className="kp-dash-mini kp-dash-mini--warn">
              <span className="kp-dash-mini__label">Rapor bekleyen (bugün)</span>
              <strong>{overview.today_reports_summary.pending_report}</strong>
            </div>
          </div>

          <div
            className={`kp-dash-panels${canSeeVehicleAlerts ? " kp-dash-panels--triple" : ""}`}
          >
            <section className="kp-dash-panel kp-card">
              <header className="kp-dash-panel__head">
                <h2>Yaklaşan Randevular</h2>
                <Link to="/calendar" className="kp-dash-panel__link">
                  Tümünü gör →
                </Link>
              </header>
              {overview.upcoming.length === 0 ? (
                <div className="kp-dash-empty">
                  <p>Önümüzdeki saatlerde planlı randevu yok.</p>
                  <Link to="/calendar" className="kp-btn">
                    Takvimi Aç
                  </Link>
                </div>
              ) : (
                <ul className="kp-dash-upcoming">
                  {overview.upcoming.map((item, idx) => (
                    <li
                      key={item.appointment_id}
                      className={`kp-dash-upcoming__item${idx === 0 ? " kp-dash-upcoming__item--next" : ""}`}
                    >
                      <div className="kp-dash-upcoming__time">
                        <strong>{formatTimeHm(item.starts_at)}</strong>
                        <span>{formatTimeHm(item.ends_at)}</span>
                        {idx === 0 && <em className="kp-dash-upcoming__badge">İlk ders</em>}
                      </div>
                      <div className="kp-dash-upcoming__body">
                        <p className="kp-dash-upcoming__student">{item.student_name}</p>
                        <p className="kp-dash-upcoming__meta">
                          {item.instructor_name} · {item.vehicle_plate} ·{" "}
                          {LESSON_TYPE_LABEL[item.lesson_type] || item.lesson_type}
                        </p>
                        <span className="kp-dash-upcoming__until">{formatUntilStart(item.starts_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="kp-dash-panel kp-card">
              <header className="kp-dash-panel__head">
                <h2>Bugünün Ders Raporları</h2>
                <span className="kp-dash-panel__badge">
                  {overview.today_reports_summary.with_report}/{overview.today_reports_summary.total_lessons} rapor
                </span>
              </header>
              {overview.today_lessons.length === 0 ? (
                <div className="kp-dash-empty">
                  <p>Bugün için kayıtlı ders yok.</p>
                </div>
              ) : (
                <ul className="kp-dash-reports">
                  {overview.today_lessons.map((lesson) => {
                    const ended =
                      lesson.status === "completed" || new Date(lesson.ends_at).getTime() <= Date.now();
                    const needsReport = ended && !lesson.has_report;
                    return (
                      <li key={lesson.appointment_id} className="kp-dash-reports__item">
                        <div className="kp-dash-reports__main">
                          <p className="kp-dash-reports__name">{lesson.student_name}</p>
                          <p className="kp-dash-reports__meta">
                            {formatTimeHm(lesson.starts_at)}–{formatTimeHm(lesson.ends_at)} · {lesson.instructor_name}
                          </p>
                          <span
                            className={`kp-dash-reports__status${
                              lesson.has_report
                                ? " kp-dash-reports__status--ok"
                                : needsReport
                                  ? " kp-dash-reports__status--pending"
                                  : ""
                            }`}
                          >
                            {lesson.has_report
                              ? lesson.report_attended === false
                                ? "Rapor: gelmedi"
                                : "Rapor girildi"
                              : needsReport
                                ? "Rapor bekliyor"
                                : "Planlandı"}
                          </span>
                        </div>
                        <button
                          type="button"
                          className={`kp-dash-reports__btn${needsReport ? " kp-dash-reports__btn--primary" : ""}`}
                          onClick={() => setLessonReportFor(lesson)}
                        >
                          {lesson.has_report ? "Raporu Aç" : "Ders Raporu"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {lessonsNeedingReport.length > 0 && (
                <p className="kp-dash-panel__foot">
                  {lessonsNeedingReport.length} ders için rapor henüz girilmedi.
                </p>
              )}
            </section>

            {canSeeVehicleAlerts && (
              <section className="kp-dash-panel kp-card kp-dash-notify">
                <header className="kp-dash-panel__head kp-dash-notify__head">
                  <div className="kp-dash-notify__title-wrap">
                    <span className="kp-dash-notify__bell" aria-hidden>
                      🔔
                    </span>
                    <h2>Bildirimler</h2>
                    {dashboardNotifyBadgeCount > 0 && (
                      <span className="kp-dash-notify__badge">{dashboardNotifyBadgeCount}</span>
                    )}
                  </div>
                  <Link to="/vehicles" className="kp-dash-panel__link">
                    Araçlar →
                  </Link>
                </header>

                {notificationsLoading && (
                  <div className="kp-dash-empty kp-dash-empty--compact">
                    <p>Bildirimler yükleniyor…</p>
                  </div>
                )}
                {notificationsError && (
                  <div className="kp-error kp-dash-notify__error">{notificationsError}</div>
                )}

                {!notificationsLoading && !notificationsError && dashboardNotifyItems.length === 0 && (
                  <div className="kp-dash-empty kp-dash-empty--compact">
                    <p>Açık bildirim yok.</p>
                  </div>
                )}

                {!notificationsLoading && !notificationsError && dashboardNotifyItems.length > 0 && (
                  <div className="kp-dash-notify__sections">
                    {dashboardNotifyUnread.length > 0 && (
                      <section>
                        <h3 className="kp-dash-notify__section-title">Okunmamış</h3>
                        <ul className="kp-dash-notify__list">
                          {dashboardNotifyUnread.map((item) => (
                            <li
                              key={item.key}
                              className={`kp-dash-notify__item kp-dash-notify__item--unread kp-dash-notify__item--${item.severity}`}
                            >
                              <div className="kp-dash-notify__item-top">
                                <strong className="kp-dash-notify__plate">{item.plate}</strong>
                                <div className="kp-dash-notify__item-actions">
                                  <span className="kp-dash-notify__time">{formatNotifyTime(item.time)}</span>
                                  {item.notificationId && canSeeNotifications && (
                                    <button
                                      type="button"
                                      className="kp-dash-notify__read-btn"
                                      title="Okundu"
                                      aria-label="Okundu"
                                      onClick={() => notifications.markAsRead(item.notificationId!)}
                                    >
                                      ✓
                                    </button>
                                  )}
                                </div>
                              </div>
                              {item.label && (
                                <span
                                  className={`kp-dash-notify__tag kp-dash-notify__tag--${item.severity}`}
                                >
                                  {item.label}
                                </span>
                              )}
                              {item.message && <p className="kp-dash-notify__msg">{item.message}</p>}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    {dashboardNotifyRead.length > 0 && (
                      <section>
                        <h3 className="kp-dash-notify__section-title kp-dash-notify__section-title--read">
                          Okundu
                        </h3>
                        <ul className="kp-dash-notify__list">
                          {dashboardNotifyRead.map((item) => (
                            <li
                              key={item.key}
                              className={`kp-dash-notify__item kp-dash-notify__item--read kp-dash-notify__item--${item.severity}`}
                            >
                              <div className="kp-dash-notify__item-top">
                                <span className="kp-dash-notify__plate">{item.plate}</span>
                                <div className="kp-dash-notify__item-actions">
                                  <span className="kp-dash-notify__time">{formatNotifyTime(item.time)}</span>
                                  {item.notificationId && canSeeNotifications && (
                                    <button
                                      type="button"
                                      className="kp-dash-notify__read-btn kp-dash-notify__read-btn--marked"
                                      title="Okunmadı olarak işaretle"
                                      aria-label="Okunmadı olarak işaretle"
                                      onClick={() => notifications.markAsUnread(item.notificationId!)}
                                    >
                                      ✓
                                    </button>
                                  )}
                                </div>
                              </div>
                              {item.label && (
                                <span
                                  className={`kp-dash-notify__tag kp-dash-notify__tag--${item.severity}`}
                                >
                                  {item.label}
                                </span>
                              )}
                              {item.message && (
                                <p className="kp-dash-notify__msg kp-dash-notify__msg--read">{item.message}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                  </div>
                )}
              </section>
            )}
          </div>
        </>
      )}

      {lessonReportFor && (
        <LessonReportModal
          appointmentId={lessonReportFor.appointment_id}
          studentName={lessonReportFor.student_name}
          instructorName={lessonReportFor.instructor_name}
          vehiclePlate={lessonReportFor.vehicle_plate}
          startsAt={lessonReportFor.starts_at}
          endsAt={lessonReportFor.ends_at}
          onClose={() => setLessonReportFor(null)}
          onSaved={() => {
            api
              .get<DashboardOverview>("/dashboard/overview", {
                day_offset: String(dayOffset),
                week_offset: String(weekOffset),
                month_offset: String(monthOffset),
              })
              .then(setOverview)
              .catch(() => undefined);
          }}
        />
      )}
    </div>
  );
}
